import { Box, Container, Link } from "@mui/material";
import { createTheme, ThemeProvider, lighten } from "@mui/material/styles";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { CTextField } from "@/components/TextField/CTextField";
import { CTextFieldPassword } from "@/components/TextField/CTextFieldPassword";
import { SubmitButton, ButtonVariantOption } from "@/components/Button";
import type { ILoginPresenterProps } from "./types/index";
import type { LoginFormData } from "./types/schemas";
import React from "react";
import LoginIcon from "@mui/icons-material/Login";
import { format } from "date-fns";
import ja from "date-fns/locale/ja";
import { NewYearHolidayNotice } from "./components/NewYearHolidayNotice";
import { LinkButton } from "@/components/Button/LinkButton";
import { ButtonColorOption } from "@/components/Button/Button";
import NoteAdd from "@mui/icons-material/NoteAdd";
import { blueGrey } from "@mui/material/colors";
import logo_color from "../../assets/logo_color.svg";
const theme = createTheme();

const LoginPresenter: React.FC<ILoginPresenterProps> = (props) => {
  const { loginForm, handleSubmit } = props;

  // ログインボタンをスクリプトからクリックするために使用するref
  const ref = React.useRef<HTMLButtonElement>(null);

  // テキストボックスの有効/無効を制御するstate
  const [loading, setLoading] = React.useState(false);

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      ref.current?.click();
    }
  };

  // ログインUIのリセット
  const resetLoginUI = (cancelLoading: () => void) => {
    return () => {
      cancelLoading();
      setLoading(false);
    };
  };

  const handleClick = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): void => {
    setLoading(true);

    void loginForm.handleSubmit(
      async (data: LoginFormData) => {
        await handleSubmit(data, resetLoginUI(cancelLoading));
      },
      () => {
        // バリデーションエラー時の処理
        resetLoginUI(cancelLoading)();
      }
    )();
  };

  const today = new Date();
  const notifyStartDate = new Date(2025, 11, 22);
  const notifyEndDate = new Date(2026, 0, 5);
  const holidayStartDateText = format(
    new Date(2025, 11, 31),
    "yyyy年MM月dd日（eee）",
    { locale: ja }
  );
  const holidayEndDateText = format(
    new Date(2026, 0, 4),
    "yyyy年MM月dd日（eee）",
    {
      locale: ja,
    }
  );
  const businessStartDateText = format(notifyEndDate, "yyyy年MM月dd日（eee）", {
    locale: ja,
  });

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          py: 10,
          backgroundColor: lighten(blueGrey[50], 0.8),
          alignItems: "center",
        }}
      >
        {/* ロゴ */}
        <Box
          sx={{
            flexGrow: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 4,
          }}
        >
          <img
            src={logo_color}
            alt="logo"
            height="48px"
            width="90%"
            style={{
              marginBottom: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          />
        </Box>
        {/* ログインフォームコンテナ */}
        <Container
          maxWidth="xs"
          sx={{
            backgroundColor: "white",
            borderRadius: 6,
            border: { xs: "none", sm: "1px solid rgba(0, 0, 0, 0.1)" },
            py: { xs: 4, sm: 6 },
            px: { xs: 2, sm: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <form>
            {/* メールアドレス */}
            <Box sx={{ my: 2, mb: 1 }}>
              <CTextField
                control={loginForm.control}
                name="email"
                id="email"
                label="メールアドレス"
                required={false}
                autoFocus={true}
                size={SizingWrapperStyle.LARGE}
                disabled={loading}
                center={true}
              />
            </Box>
            {/* パスワード */}
            <Box sx={{ my: 2, mb: 1 }}>
              <CTextFieldPassword
                control={loginForm.control}
                name="password"
                autoComplete="current-password"
                required={false}
                onKeyUp={handleKeyUp}
                size={SizingWrapperStyle.LARGE}
                disabled={loading}
                center={true}
              />
            </Box>

            {/* ログインボタン */}
            <Box sx={{ my: 3 }}>
              <SubmitButton
                label={"ログイン"}
                onClick={handleClick}
                variant={ButtonVariantOption.Contained}
                size={SizingWrapperStyle.LARGE}
                loadingIndicator={"ログイン中..."}
                timeout={30000}
                refToClickButton={ref}
                onTimeout={() => {
                  setLoading(false);
                }}
                startIcon={<LoginIcon />}
                sx={{
                  fontWeight: 600,
                  p: 1.5,
                  borderRadius: 2,
                }}
              />
            </Box>
          </form>
          {/* プライバシーポリシー・利用規約 */}
          <Box
            sx={{
              mt: 2,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Link
              href="https://www.trustart.co.jp/privacy/"
              sx={{ display: "inline-block", fontSize: 13 }}
              target="_blank"
              rel="noopener"
            >
              プライバシーポリシー
            </Link>
            <Link
              href="https://www.trustart.co.jp/redata_plus_terms/"
              sx={{ display: "inline-block", mt: 1, fontSize: 13 }}
              target="_blank"
              rel="noopener"
            >
              サービス利用規約
            </Link>
          </Box>
        </Container>
        {/* アカウント開設申込フォーム */}
        <Box
          sx={{
            mt: 4,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <LinkButton
            variant={ButtonVariantOption.Outlined}
            href="https://lp.redata-plus.com/application/request?utm_source=redata-plus.com&utm_medium=referral&utm_campaign=application&utm_term=pre&utm_content=login"
            wrapperSize={SizingWrapperStyle.LARGE}
            size={SizingWrapperStyle.LARGE}
            color={ButtonColorOption.Success}
            target="_blank"
            rel="noopener"
            sx={{
              p: 2,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              justifyContent: "center",
              fontSize: { xs: "12px", sm: "16px" },
              width: { xs: "80%", sm: "100%" },
              mx: "auto",
            }}
          >
            <NoteAdd fontSize="medium" />
            <span>アカウント開設の仮申込はこちら</span>
          </LinkButton>
        </Box>
        {/* 年末年始休業のお知らせ */}
        {today >= notifyStartDate && today <= notifyEndDate && (
          <Box
            sx={{
              mt: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <NewYearHolidayNotice
              holidayStartDateText={holidayStartDateText}
              holidayEndDateText={holidayEndDateText}
              businessStartDateText={businessStartDateText}
            />
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export { LoginPresenter };
